import axios from 'axios';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// ===================== SECURITY & PERFORMANCE CONFIG =====================
const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Create axios instance with enhanced config
const api = axios.create({
  baseURL: API_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // CSRF protection
  },
});

// ===================== REQUEST INTERCEPTOR =====================
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };
    
    return config;
  },
  (error) => {
    logger.error('Request error:', error);
    return Promise.reject(error);
  }
);

// ===================== RESPONSE INTERCEPTOR WITH RETRY LOGIC =====================
api.interceptors.response.use(
  (response) => {
    // Log response time for performance monitoring
    const duration = new Date() - response.config.metadata?.startTime;
    if (duration > 5000) {
      logger.warn(`Slow API response: ${response.config.url} took ${duration}ms`);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
      return Promise.reject(error);
    }
    
    // Handle 429 Rate Limited - wait and retry
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 5;
      await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
      return api(originalRequest);
    }
    
    // Retry logic for network errors and 5xx errors
    if (
      !originalRequest._retry &&
      originalRequest._retryCount < MAX_RETRIES &&
      (error.code === 'ECONNABORTED' || 
       error.code === 'ERR_NETWORK' ||
       (error.response?.status >= 500 && error.response?.status < 600))
    ) {
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      
      // Exponential backoff
      const delay = RETRY_DELAY * Math.pow(2, originalRequest._retryCount - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      logger.log(`Retrying request (${originalRequest._retryCount}/${MAX_RETRIES}): ${originalRequest.url}`);
      return api(originalRequest);
    }
    
    // Enhanced error logging
    if (error.response) {
      logger.error(`API Error ${error.response.status}: ${error.config?.url}`, error.response.data);
    } else if (error.request) {
      logger.error('Network Error: No response received', error.config?.url);
    } else {
      logger.error('Request Setup Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),

  // File Upload
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Packages
  getPackages: () => api.get('/admin/packages'),
  getPackage: (id) => api.get(`/admin/packages/${id}`),
  createPackage: (data) => api.post('/admin/packages', data),
  updatePackage: (id, data) => api.put(`/admin/packages/${id}`, data),
  deletePackage: (id) => api.delete(`/admin/packages/${id}`),

  // Services
  getServices: () => api.get('/admin/services'),
  getService: (id) => api.get(`/admin/services/${id}`),
  createService: (data) => api.post('/admin/services', data),
  updateService: (id, data) => api.put(`/admin/services/${id}`, data),
  deleteService: (id) => api.delete(`/admin/services/${id}`),

  // Testimonials
  getTestimonials: () => api.get('/admin/testimonials'),
  getTestimonial: (id) => api.get(`/admin/testimonials/${id}`),
  createTestimonial: (data) => api.post('/admin/testimonials', data),
  updateTestimonial: (id, data) => api.put(`/admin/testimonials/${id}`, data),
  deleteTestimonial: (id) => api.delete(`/admin/testimonials/${id}`),

  // Inquiries
  getInquiries: (status) => api.get('/admin/inquiries', { params: { status } }),
  getInquiryStats: () => api.get('/admin/inquiries/stats'),
  getInquiry: (id) => api.get(`/admin/inquiries/${id}`),
  updateInquiry: (id, data) => api.put(`/admin/inquiries/${id}`, data),
  deleteInquiry: (id) => api.delete(`/admin/inquiries/${id}`),

  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),

  // Gallery
  getGallery: () => api.get('/admin/gallery'),
  getGalleryImage: (id) => api.get(`/admin/gallery/${id}`),
  createGalleryImage: (data) => api.post('/admin/gallery', data),
  updateGalleryImage: (id, data) => api.put(`/admin/gallery/${id}`, data),
  deleteGalleryImage: (id) => api.delete(`/admin/gallery/${id}`),

  // Loyalty Program
  getLoyaltyProgram: () => api.get('/admin/loyalty-program'),
  updateLoyaltyProgram: (data) => api.put('/admin/loyalty-program', data),

  // Blogs
  getBlogs: () => api.get('/admin/blogs'),
  getBlog: (id) => api.get(`/admin/blogs/${id}`),
  createBlog: (data) => api.post('/admin/blogs', data),
  updateBlog: (id, data) => api.put(`/admin/blogs/${id}`, data),
  deleteBlog: (id) => api.delete(`/admin/blogs/${id}`),

  // Downloads
  getDownloads: () => api.get('/admin/downloads'),
  createDownload: (data) => api.post('/admin/downloads', data),
  updateDownload: (id, data) => api.put(`/admin/downloads/${id}`, data),
  deleteDownload: (id) => api.delete(`/admin/downloads/${id}`),

  // Downloads Page Settings
  getDownloadsPageSettings: () => api.get('/admin/downloads-page-settings'),
  updateDownloadsPageSettings: (data) => api.put('/admin/downloads-page-settings', data),

  // Contact Info
  getContactInfo: () => api.get('/admin/contact-info'),
  updateContactInfo: (data) => api.put('/admin/contact-info', data),

  // Test Details
  getTestDetails: () => api.get('/admin/test-details'),
  getTestDetail: (id) => api.get(`/admin/test-details/${id}`),
  createTestDetail: (data) => api.post('/admin/test-details', data),
  updateTestDetail: (id, data) => api.put(`/admin/test-details/${id}`, data),
  deleteTestDetail: (id) => api.delete(`/admin/test-details/${id}`),

  // Hero Slides
  getHeroSlides: () => api.get('/admin/hero-slides'),
  createHeroSlide: (data) => api.post('/admin/hero-slides', data),
  updateHeroSlide: (id, data) => api.put(`/admin/hero-slides/${id}`, data),
  deleteHeroSlide: (id) => api.delete(`/admin/hero-slides/${id}`),

  // Directors (About Page)
  getDirectors: () => api.get('/admin/directors'),
  createDirector: (data) => api.post('/admin/directors', data),
  updateDirector: (id, data) => api.put(`/admin/directors/${id}`, data),
  deleteDirector: (id) => api.delete(`/admin/directors/${id}`),

  // Consultants (About Page)
  getConsultants: () => api.get('/admin/consultants'),
  createConsultant: (data) => api.post('/admin/consultants', data),
  updateConsultant: (id, data) => api.put(`/admin/consultants/${id}`, data),
  deleteConsultant: (id) => api.delete(`/admin/consultants/${id}`),

  // Differentiators (About Page - What Makes Us Different)
  getDifferentiators: () => api.get('/admin/differentiators'),
  createDifferentiator: (data) => api.post('/admin/differentiators', data),
  updateDifferentiator: (id, data) => api.put(`/admin/differentiators/${id}`, data),
  deleteDifferentiator: (id) => api.delete(`/admin/differentiators/${id}`),

  // About Page Settings
  getAboutSettings: () => api.get('/admin/about-settings'),
  updateAboutSettings: (data) => api.put('/admin/about-settings', data),

  // Hero Directors (for hero section icons)
  getHeroDirectors: () => api.get('/admin/hero-directors'),
  updateHeroDirector: (id, data) => api.put(`/admin/hero-directors/${id}`, data),

  // Audit Logs
  getAuditLogs: (limit = 100) => api.get('/admin/audit-logs', { params: { limit } }),
};

// Public API
export const publicAPI = {
  getPackages: () => api.get('/public/packages'),
  getPackageBySlug: (slug) => api.get(`/public/packages/${slug}`),
  getServices: () => api.get('/public/services'),
  getServiceBySlug: (slug) => api.get(`/public/services/${slug}`),
  getTestimonials: () => api.get('/public/testimonials'),
  getSettings: () => api.get('/public/settings'),
  getGallery: () => api.get('/public/gallery'),
  submitInquiry: (data) => api.post('/public/inquiries', data),
  getLoyaltyProgram: () => api.get('/public/loyalty-program'),
  getBlogs: () => api.get('/public/blogs'),
  getBlogBySlug: (slug) => api.get(`/public/blogs/${slug}`),
  getDownloads: () => api.get('/public/downloads'),
  getContactInfo: () => api.get('/public/contact-info'),
  getDownloadsPageSettings: () => api.get('/public/downloads-page-settings'),
  searchReport: (data) => api.post('/public/reports/search', { lab_id: data.labId, password: data.password }),
  getTestDetails: () => api.get('/public/test-details'),
  getTestDetailBySlug: (slug) => api.get(`/public/test-details/${slug}`),
  getHeroSlides: () => api.get('/public/hero-slides'),
  getHeroDirectors: () => api.get('/public/hero-directors'),
  getDirectors: () => api.get('/public/directors'),
  getConsultants: () => api.get('/public/consultants'),
  getDifferentiators: () => api.get('/public/differentiators'),
  getAboutSettings: () => api.get('/public/about-settings'),
};

export default api;
